import React from 'react';

import { Typography, useTheme } from '@material-ui/core';

import { getColor } from '@vyce/core/src/theme/styles';
import { DRC_REASON_CODES, DRCStatus } from '@vyce/core/src/types';
import { DRC_REASON_LABELS } from '@vyce/core/src/constants';

import { useStyles } from '../styles';

interface Props {
  status: DRCStatus | null;
}

/**
 * The one-line answer to "does the reverse charge apply to this worker today". `applies` is
 * derived per request from the as-of date, so it is read off the response rather than computed
 * here — the client must not have its own opinion about a tax decision.
 */
export const DRCStatusChip: React.FC<Props> = ({ status }) => {
  const classes = useStyles();
  const theme = useTheme();
  const colors = getColor(theme.palette.type === 'dark');

  if (!status) {
    return null;
  }

  if (status.applies) {
    return (
      <Typography
        component="span"
        className={classes.chip}
        style={{ background: colors.pillOnBG, color: colors.pillOnText }}>
        Reverse charge · applies
      </Typography>
    );
  }

  // A number flagged as registered but never checked against HMRC is a problem to fix, not a
  // setting left alone — it is the only reason code shown in the error tone.
  const isError = status.reason_code === DRC_REASON_CODES.VAT_NOT_VERIFIED;
  const reason = status.reason_code ? DRC_REASON_LABELS[status.reason_code] : 'not assessed';

  return (
    <Typography
      component="span"
      className={classes.chip}
      style={
        isError
          ? { background: colors.errorBG, color: colors.errorDark }
          : { background: colors.pillOffBG, color: colors.pillOffText }
      }>
      {`Reverse charge · ${reason.toLowerCase()}`}
    </Typography>
  );
};
